import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import { MaterialModule } from '../../material/material.module';
import { VideoComponent } from '../video/video.component';
import { environment } from '../../../environments/environment';
import { HELP_ITEM_TYPE } from '../../common/help-model';
import { OrganisationService } from '../../organisation/organisation.service';
import { MODEL_TYPE, NODE_SUPERTYPE, NODE_TYPE, TUTORIAL_MODEL_KEY } from '../../common/linqset-model';
import { Router } from '@angular/router';
import { SKETCH_URL } from '../../app-urls';
import { Currencies, Currency, FREE_NODES, FREE_PROJECTS, FREE_USERS, getCurrency } from '../../common/organisation';
import { RepositoryService } from '../../linq-utils/repository.service';

interface OnboardingStep {
    label:string                // on the step menu
    title:string
    helpType:HELP_ITEM_TYPE
    media:'video'|'image'
}

const STEPS:OnboardingStep[] = [
    {label:'Intro', title:'Introduction', helpType:HELP_ITEM_TYPE.INTRODUCTION, media:'video'},
    {label:'Organisations', title:'Organisations and workspaces', helpType:HELP_ITEM_TYPE.HOME_SCREEN, media:'video'},
    {label:'Language', title:'LINQ language', helpType:HELP_ITEM_TYPE.LINQ_LANGUAGE, media:'video'},
    {label:'Models', title:'Create a model', helpType:HELP_ITEM_TYPE.MODEL_SCREEN, media:'video'},
    {label:'Quantify', title:'Quantify your model', helpType:HELP_ITEM_TYPE.QUANTIFY_MODEL, media:'video'},
    {label:'People', title:'People and systems', helpType:HELP_ITEM_TYPE.PEOPLE_SYSTEMS, media:'video'},
    {label:'Insights', title:'Insights', helpType:HELP_ITEM_TYPE.INSIGHTS, media:'image'},
    {label:'Projects', title:'Projects', helpType:HELP_ITEM_TYPE.PROJECTS, media:'image'},
    {label:'Demo', title:'Demo organisation', helpType:HELP_ITEM_TYPE.DEMO_ORG, media:'image'},
    {label:'Billing', title:'Billing', helpType:HELP_ITEM_TYPE.BILLING, media:'image'},
]

// a step by step guide to LINQ, each step with some text and a video or picture
@Component({
    selector: 'app-onboarding',
    templateUrl: 'onboarding.html',
    styleUrls: ['onboarding.css'],
    imports: [MaterialModule, VideoComponent],
    standalone: true
})
export class OnboardingComponent implements OnChanges {
    @Input() initialStep:number = 1

    steps = STEPS
    step:number = 1
    helpType:HELP_ITEM_TYPE = HELP_ITEM_TYPE.INTRODUCTION
    videoUrl:string
    imageUrl:string

    NODE_SUPERTYPE = NODE_SUPERTYPE
    NODE_TYPE = NODE_TYPE
    FREE_NODES = FREE_NODES
    FREE_USERS = FREE_USERS
    FREE_PROJECTS = FREE_PROJECTS
    currency:Currency

    @Output() closeEvent:EventEmitter<string> = new EventEmitter<string>()

    constructor(private organisationService:OrganisationService, private router:Router, private repoService:RepositoryService) {
        this.setStep(1)
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialStep']) this.setStep(this.initialStep || 1)
    }

    get current() {
        return STEPS[this.step - 1]
    }

    // the arrow keys move between steps, unless a video or other control has focus
    @HostListener('keydown', ['$event'])
    onKeydown(event:KeyboardEvent) {
        const target = event.target as HTMLElement
        if (target?.closest('video, input, textarea, select, [role=slider]')) return
        if (event.key === 'ArrowRight' && this.step < STEPS.length) this.setStep(this.step + 1)
        else if (event.key === 'ArrowLeft' && this.step > 1) this.setStep(this.step - 1)
    }

    closePanel() {
        this.closeEvent.emit('')
    }

    getColour() {
        if (environment.production) return "primary"
        else return "accent"
    }

    setStep(step:number) {
        this.step = Math.min(Math.max(1, step), STEPS.length)
        const current = this.current
        this.helpType = current.helpType
        this.videoUrl = current.media === 'video' ? `api/help/${current.helpType}/video` : undefined
        // the same server address the videos use
        this.imageUrl = current.media === 'image' ? this.repoService.createCompleteRoute(`api/help/${current.helpType}/image`) : undefined
        this.currency = this.getOrganisation()?getCurrency(this.getOrganisation().currencyCode):Currencies[0]
    }

    getOrganisation() {
        return this.organisationService.organisation
    }

    gotoTutorial() {
        if (this.organisationService.organisation) {
            this.router.navigateByUrl(SKETCH_URL(this.organisationService.organisation._key, TUTORIAL_MODEL_KEY, MODEL_TYPE.PROCESS))
            this.closePanel()
        }
    }
}
